import { applyMargin, DEFAULT_MARGIN_BPS, microToUsd } from "./money.js";

/**
 * Which assistant answers in the Studio's chat box (04, `studio_turn`).
 *
 * Each has more than one adapter behind it, and the customer chooses. It is the only place in the product where that
 * choice is theirs: the pipeline picks its own models per step, because a customer has no way to know which image
 * model handles a bakery counter well and no reason to care.
 *
 * The prices are per million tokens, from each vendor's own list, and they are here rather than only in
 * `pricing.json` because the picker shows them. Somebody choosing between assistants is choosing between costs,
 * and hiding that would make the control decorative.
 */
export interface ChatModel {
  /** `provider:model`, which is what is stored and what names the adapter. */
  id: string;
  provider: "openai" | "anthropic" | "xai";
  model: string;
  /** The vendor, as a person says it. */
  maker: string;
  /** The model, as a person says it. */
  name: string;
  /** One line: what it is good at, or what it costs you. */
  note: string;
  inPerMTok: number;
  outPerMTok: number;
}

/**
 * The catalogue. Model ids are the vendors' own and move faster than this file: each adapter takes its id from the
 * environment first, so a rename is a secret and not a deploy.
 */
export const CHAT_MODELS: readonly ChatModel[] = [
  { id: "openai:gpt-5.2", provider: "openai", model: "gpt-5.2", maker: "ChatGPT", name: "GPT-5.2", note: "The default. Quick, and good at short questions.", inPerMTok: 1.75, outPerMTok: 14 },
  { id: "openai:gpt-5.2-mini", provider: "openai", model: "gpt-5.2-mini", maker: "ChatGPT", name: "GPT-5.2 mini", note: "Cheapest here. Fine for filling in a form.", inPerMTok: 0.25, outPerMTok: 2 },
  { id: "anthropic:claude-sonnet-5", provider: "anthropic", model: "claude-sonnet-5", maker: "Claude", name: "Sonnet 5", note: "Writes the most natural English of these.", inPerMTok: 2, outPerMTok: 10 },
  { id: "anthropic:claude-opus-5", provider: "anthropic", model: "claude-opus-5", maker: "Claude", name: "Opus 5", note: "The most careful, and the dearest.", inPerMTok: 5, outPerMTok: 25 },
  { id: "xai:grok-4", provider: "xai", model: "grok-4", maker: "Grok", name: "Grok 4", note: "Blunter. Some people prefer it.", inPerMTok: 3, outPerMTok: 15 },
];

/** ChatGPT, unless the workspace says otherwise. */
export const DEFAULT_CHAT_MODEL = "openai:gpt-5.2";

export const chatModel = (id: string | null | undefined): ChatModel =>
  CHAT_MODELS.find((m) => m.id === id) ?? CHAT_MODELS.find((m) => m.id === DEFAULT_CHAT_MODEL)!;

/**
 * What one chat turn usually reads and writes. The first real Business turns, on GPT-5.2 on the dev box (2026-09-12),
 * read about 1,730 tokens and wrote 94. A Studio turn reads more, because it carries the photo list, and writes more,
 * because it answers with the brief as well as a reply, so both are rounded up.
 */
export const TYPICAL_TURN = { tokensIn: 2_000, tokensOut: 150 } as const;

/**
 * What a hundred messages are charged, near enough, for the picker to put a number beside a name: a typical turn at the
 * model's list price, with the margin every customer call carries, worked out as the charge is (`applyMargin`, on the
 * turn, in micro-dollars, rounded up). The default margin, because the picker is drawn in the browser and no box sets
 * its own (`DEFAULT_MARGIN_BPS`): 20% since 2026-10-02, when ChatGPT went from $0.78 a hundred to $0.67.
 *
 * Until 2026-09-12 it left the margin out and counted three times the output a turn writes, so it said $0.68 for ChatGPT
 * where a hundred of those Business turns were charged $0.61.
 */
export function chatPriceHint(m: ChatModel, marginBps: number = DEFAULT_MARGIN_BPS): string {
  // A price per million tokens times a count of tokens is micro-dollars.
  const cost = BigInt(Math.ceil(m.inPerMTok * TYPICAL_TURN.tokensIn + m.outPerMTok * TYPICAL_TURN.tokensOut));
  return `about $${microToUsd(applyMargin(cost, marginBps) * 100n)} per 100 messages`;
}
